import React, { useState, useEffect } from 'react';
import { useMode } from '../contexts/ModeContext';
import { useNavigate } from 'react-router-dom';
import { getTranslation } from '../translations';
import { settingsAPI, offlineAPI, authAPI, accountAPI, consentsAPI } from '../utils/api';
import { ArrowLeft, Globe, Palette, Key, Download, Wifi, WifiOff, Target, Award, Shield, Trash2, FileText, Bell, Mic, Mail } from 'lucide-react';
import { toast } from 'sonner';

const Settings = () => {
  const { isKidsMode, language, changeLanguage, toggleMode, setMode } = useMode();
  const navigate = useNavigate();
  const t = (key) => getTranslation(language, key);
  
  const [apiKey, setApiKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);
  const [cachedSurahs, setCachedSurahs] = useState([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [practiceMode, setPracticeMode] = useState('child');
  const [difficultyLevel, setDifficultyLevel] = useState('medium');
  
  // Detect if running on mobile device
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  useEffect(() => {
    fetchUser();
    fetchCachedSurahs();
    
    // Online/Offline listeners
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const fetchUser = async () => {
    try {
      const res = await authAPI.getMe();
      setUser(res.data);
      const userPracticeMode = res.data.practice_mode || 'child';
      setPracticeMode(userPracticeMode);
      // Keep visual theme in sync with practice mode to avoid the
      // "Yetişkin teması ama Çocuk modu" confusion.
      setMode(userPracticeMode === 'adult' ? 'adults' : 'kids');
      setDifficultyLevel(res.data.difficulty_level || 'medium');
      if (res.data.openai_api_key) {
        setApiKey('••••••••••••••••');
      }
    } catch (error) {
      console.error('User fetch error:', error);
    }
  };

  const fetchCachedSurahs = async () => {
    try {
      const res = await offlineAPI.getCachedSurahs();
      setCachedSurahs(res.data);
    } catch (error) {
      console.error('Cache fetch error:', error);
    }
  };

  const saveSettings = async (updates) => {
    setSaving(true);
    try {
      await settingsAPI.updateSettings(updates);
      toast.success('Ayarlar kaydedildi!');
      await fetchUser();
    } catch (error) {
      toast.error('Kaydetme başarısız');
      console.error('Settings save error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handlePracticeModeChange = async (mode) => {
    setPracticeMode(mode);
    // Auto-sync visual theme so Adult mode never shows Kids UI (and vice versa)
    setMode(mode === 'adult' ? 'adults' : 'kids');
    await saveSettings({ practice_mode: mode });
  };

  // Theme toggle that ALSO updates practice_mode so the two never drift apart.
  const handleThemeToggle = async () => {
    const newPractice = isKidsMode ? 'adult' : 'child';
    await handlePracticeModeChange(newPractice);
  };

  const handleDifficultyChange = async (level) => {
    setDifficultyLevel(level);
    await saveSettings({ difficulty_level: level });
  };

  const cacheSurahForOffline = async (surahNumber) => {
    try {
      await offlineAPI.cacheSurah(surahNumber);
      toast.success(`Sure ${surahNumber} offline için kaydedildi`);
      fetchCachedSurahs();
    } catch (error) {
      toast.error('Kaydetme başarısız');
    }
  };

  return (
    <div
      className="min-h-screen"
      style={{
        background: isKidsMode
          ? 'linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)'
          : 'linear-gradient(135deg, #F5F5F4 0%, #E7E5E4 100%)'
      }}
    >
      <header className="px-6 py-4 bg-white/50 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1
            className={`text-2xl font-bold ${
              isKidsMode ? 'font-kids text-green-500' : 'font-elegant text-emerald-900'
            }`}
          >
            {t('settings')}
          </h1>
          
          {/* Online Status Indicator */}
          <div className="ml-auto flex items-center gap-2">
            {isOnline ? (
              <>
                <Wifi className="w-5 h-5 text-green-600" />
                <span className="text-sm text-green-600">Çevrimiçi</span>
              </>
            ) : (
              <>
                <WifiOff className="w-5 h-5 text-red-600" />
                <span className="text-sm text-red-600">Çevrimdışı</span>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="space-y-6">
          {/* Practice Mode Settings */}
          <div
            className={`p-6 ${
              isKidsMode
                ? 'bg-white rounded-3xl shadow-[0_8px_0_0_rgba(0,0,0,0.1)]'
                : 'bg-white/70 backdrop-blur-sm rounded-xl border border-stone-200'
            }`}
          >
            <div className="flex items-center gap-3 mb-4">
              <Target className={`w-6 h-6 ${isKidsMode ? 'text-blue-400' : 'text-blue-600'}`} />
              <h2 className={`text-xl font-bold ${isKidsMode ? 'font-kids' : 'font-elegant'}`}>
                Pratik Modu
              </h2>
            </div>
            
            {isMobile && (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                <strong>📱 Mobil Cihaz Tespit Edildi:</strong> Yetişkin Modu için server-side streaming STT kullanılacak (Whisper AI). Daha yüksek doğruluk oranı için önerilir.
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <button
                onClick={() => handlePracticeModeChange('child')}
                disabled={saving}
                className={`p-6 rounded-lg border-2 transition-all text-left ${
                  practiceMode === 'child'
                    ? isKidsMode
                      ? 'border-green-400 bg-green-50'
                      : 'border-emerald-700 bg-emerald-50'
                    : 'border-gray-200 hover:border-gray-300'
                } ${isKidsMode ? 'rounded-2xl' : ''}`}
              >
                <div className="font-bold mb-2">🧒 Çocuk Modu</div>
                <div className="text-sm text-gray-600">Ayet ayet ezber - Yeni başlayanlar için</div>
                {isMobile && (
                  <div className="mt-2 text-xs text-green-600 font-semibold">✓ Mobil için önerilir</div>
                )}
              </button>

              <button
                onClick={() => handlePracticeModeChange('adult')}
                disabled={saving}
                className={`p-6 rounded-lg border-2 transition-all text-left ${
                  practiceMode === 'adult'
                    ? isKidsMode
                      ? 'border-green-400 bg-green-50'
                      : 'border-emerald-700 bg-emerald-50'
                    : 'border-gray-200 hover:border-gray-300'
                } ${isKidsMode ? 'rounded-2xl' : ''}`}
              >
                <div className="font-bold mb-2">👨 Yetişkin Modu</div>
                <div className="text-sm text-gray-600">Sürekli hafızlık takibi - İleri seviye</div>
                {isMobile && (
                  <div className="mt-2 text-xs text-blue-600 font-semibold">✓ Streaming STT ile çalışır</div>
                )}
              </button>
            </div>

            {practiceMode === 'adult' && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg text-sm text-blue-800">
                <strong>Yetişkin Modu:</strong> Canlı dinleme, hata uyarıları, kesintisiz okuma (max 1 cüz)
                {isMobile && <div className="mt-2"><strong>Mobil:</strong> Server-side Whisper STT kullanılır</div>}
              </div>
            )}
          </div>

          {/* Difficulty Level Settings */}
          <div
            className={`p-6 ${
              isKidsMode
                ? 'bg-white rounded-3xl shadow-[0_8px_0_0_rgba(0,0,0,0.1)]'
                : 'bg-white/70 backdrop-blur-sm rounded-xl border border-stone-200'
            }`}
          >
            <div className="flex items-center gap-3 mb-4">
              <Award className={`w-6 h-6 ${isKidsMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
              <h2 className={`text-xl font-bold ${isKidsMode ? 'font-kids' : 'font-elegant'}`}>
                Zorluk Seviyesi
              </h2>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <button
                onClick={() => handleDifficultyChange('easy')}
                disabled={saving}
                className={`p-4 rounded-lg border-2 transition-all text-center ${
                  difficultyLevel === 'easy'
                    ? 'border-green-400 bg-green-50'
                    : 'border-gray-200 hover:border-gray-300'
                } ${isKidsMode ? 'rounded-2xl' : ''}`}
              >
                <div className="text-2xl mb-2">😊</div>
                <div className="font-bold mb-1">Kolay</div>
                <div className="text-xs text-gray-600">50% benzerlik</div>
              </button>

              <button
                onClick={() => handleDifficultyChange('medium')}
                disabled={saving}
                className={`p-4 rounded-lg border-2 transition-all text-center ${
                  difficultyLevel === 'medium'
                    ? 'border-blue-400 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                } ${isKidsMode ? 'rounded-2xl' : ''}`}
              >
                <div className="text-2xl mb-2">🤔</div>
                <div className="font-bold mb-1">Orta</div>
                <div className="text-xs text-gray-600">75% benzerlik</div>
              </button>

              <button
                onClick={() => handleDifficultyChange('hard')}
                disabled={saving}
                className={`p-4 rounded-lg border-2 transition-all text-center ${
                  difficultyLevel === 'hard'
                    ? 'border-red-400 bg-red-50'
                    : 'border-gray-200 hover:border-gray-300'
                } ${isKidsMode ? 'rounded-2xl' : ''}`}
              >
                <div className="text-2xl mb-2">🎓</div>
                <div className="font-bold mb-1">Zor</div>
                <div className="text-xs text-gray-600">95% benzerlik - Kurra</div>
              </button>
            </div>
          </div>

          {/* Language Settings */}
          <div
            className={`p-6 ${
              isKidsMode
                ? 'bg-white rounded-3xl shadow-[0_8px_0_0_rgba(0,0,0,0.1)]'
                : 'bg-white/70 backdrop-blur-sm rounded-xl border border-stone-200'
            }`}
            data-testid="language-settings"
          >
            <div className="flex items-center gap-3 mb-4">
              <Globe className={`w-6 h-6 ${isKidsMode ? 'text-green-400' : 'text-emerald-700'}`} />
              <h2 className={`text-xl font-bold ${isKidsMode ? 'font-kids' : 'font-elegant'}`}>
                {t('language')}
              </h2>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              {[
                { code: 'tr', label: 'Türkçe' },
                { code: 'en', label: 'English' },
                { code: 'ar', label: 'العربية' }
              ].map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    language === lang.code
                      ? isKidsMode
                        ? 'border-green-400 bg-green-50'
                        : 'border-emerald-700 bg-emerald-50'
                      : 'border-gray-200 hover:border-gray-300'
                  } ${isKidsMode ? 'rounded-2xl' : ''}`}
                  data-testid={`language-${lang.code}-button`}
                >
                  <div className="font-medium">{lang.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Theme Settings */}
          <div
            className={`p-6 ${
              isKidsMode
                ? 'bg-white rounded-3xl shadow-[0_8px_0_0_rgba(0,0,0,0.1)]'
                : 'bg-white/70 backdrop-blur-sm rounded-xl border border-stone-200'
            }`}
            data-testid="theme-settings"
          >
            <div className="flex items-center gap-3 mb-4">
              <Palette className={`w-6 h-6 ${isKidsMode ? 'text-pink-400' : 'text-teal-600'}`} />
              <h2 className={`text-xl font-bold ${isKidsMode ? 'font-kids' : 'font-elegant'}`}>
                Tema
              </h2>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={handleThemeToggle}
                disabled={isKidsMode}
                className={`p-6 rounded-lg border-2 transition-all text-left ${
                  isKidsMode
                    ? 'border-green-400 bg-green-50'
                    : 'border-gray-200 hover:border-gray-300'
                } ${isKidsMode ? 'rounded-2xl' : ''}`}
                data-testid="kids-mode-button"
              >
                <div className="font-bold mb-1">{t('kids_mode')}</div>
                <div className="text-sm text-gray-500">Renkli ve eğlenceli</div>
              </button>

              <button
                onClick={handleThemeToggle}
                disabled={!isKidsMode}
                className={`p-6 rounded-lg border-2 transition-all text-left ${
                  !isKidsMode
                    ? 'border-emerald-700 bg-emerald-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid="adult-mode-button"
              >
                <div className="font-bold mb-1">{t('adult_mode')}</div>
                <div className="text-sm text-gray-500">Sakin ve odaklı</div>
              </button>
            </div>
          </div>

          {/* Offline Mode */}
          <div
            className={`p-6 ${
              isKidsMode
                ? 'bg-white rounded-3xl shadow-[0_8px_0_0_rgba(0,0,0,0.1)]'
                : 'bg-white/70 backdrop-blur-sm rounded-xl border border-stone-200'
            }`}
            data-testid="offline-settings"
          >
            <div className="flex items-center gap-3 mb-4">
              <Download className={`w-6 h-6 ${isKidsMode ? 'text-purple-400' : 'text-purple-600'}`} />
              <h2 className={`text-xl font-bold ${isKidsMode ? 'font-kids' : 'font-elegant'}`}>
                Çevrimdışı Mod
              </h2>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              İnternet olmadan çalışmak için sureleri indirin. {cachedSurahs.length} sure indirildi.
            </p>
            
            <div className="space-y-2">
              <button
                onClick={() => cacheSurahForOffline(1)}
                disabled={!isOnline}
                className={`w-full p-3 text-left rounded-lg border transition-all ${
                  isKidsMode ? 'rounded-2xl' : ''
                } ${
                  !isOnline ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
                }`}
                data-testid="cache-fatiha-button"
              >
                <div className="font-medium">Fatiha Suresini İndir</div>
              </button>
              
              <div className="text-xs text-gray-500 mt-4">
                Not: Çevrimdışı modda sadece indirilen surelerle çalışabilirsiniz.
              </div>
            </div>
          </div>

          {/* Consent toggles (KVKK m.5/1 — açık rıza) */}
          <ConsentToggles isKidsMode={isKidsMode} />

          {/* KVKK / GDPR rights */}
          <KvkkSection isKidsMode={isKidsMode} />
        </div>
      </div>
    </div>
  );
};

// Per-purpose explicit consent toggles
const ConsentToggles = ({ isKidsMode }) => {
  const [c, setC] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    consentsAPI.get().then((r) => setC(r.data)).catch(() => setC({}));
  }, []);

  const toggle = async (key) => {
    if (!c) return;
    const next = { ...c, [key]: !c[key] };
    setC(next);
    setSaving(true);
    try {
      await consentsAPI.update({ [key]: next[key] });
    } catch {
      toast.error('Güncellenemedi');
      setC(c);
    } finally {
      setSaving(false);
    }
  };

  if (!c) return null;
  const card = `bg-white shadow-sm p-6 ${isKidsMode ? 'rounded-3xl' : 'rounded-lg'} mt-6`;
  const items = [
    { key: 'consent_push', icon: Bell, title: 'Günlük ayet ve hatırlatma bildirimleri',
      desc: 'Tarayıcı push bildirimi olarak günlük ayet ve namaz hatırlatmaları al.' },
    { key: 'consent_voice_processing', icon: Mic, title: 'Mikrofonla okuma analizi',
      desc: 'Ezber kontrolü için sesinin OpenAI Whisper ile metne dönüştürülmesine onay. Ses kalıcı saklanmaz.' },
    { key: 'consent_email_marketing', icon: Mail, title: 'Tanıtım/bilgilendirme e-postaları',
      desc: 'Yeni özellik duyuruları ve dini günlerde özel içerikler. İstediğin zaman kapatabilirsin.' },
  ];

  return (
    <div className={card} data-testid="consent-toggles-section">
      <div className="flex items-center gap-2 mb-3">
        <Shield className="w-5 h-5 text-teal-700" />
        <h2 className="text-lg font-semibold text-gray-800">Açık Rıza Tercihleri</h2>
      </div>
      <p className="text-xs text-gray-500 mb-5">
        KVKK Madde 5/1 — her veri işleme amacı için ayrı açık rıza. İstediğin zaman değiştirebilirsin.
      </p>
      <div className="space-y-3">
        {items.map(({ key, icon: Icon, title, desc }) => (
          <div key={key} className="flex items-start gap-3 p-3 rounded-lg border border-stone-200">
            <Icon className="w-5 h-5 text-stone-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-800">{title}</p>
              <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
            </div>
            <button
              type="button"
              onClick={() => toggle(key)}
              disabled={saving}
              data-testid={`consent-toggle-${key}`}
              className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors ${c[key] ? 'bg-teal-600' : 'bg-stone-300'} disabled:opacity-50`}
              aria-pressed={!!c[key]}
            >
              <span className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${c[key] ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// KVKK / GDPR section: data export + account deletion + legal links
const KvkkSection = ({ isKidsMode }) => {
  const navigate = useNavigate();
  const [exporting, setExporting] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await accountAPI.exportData();
      const blob = new Blob([res.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `hafizim_verilerim_${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Verileriniz indirildi.');
    } catch (e) {
      toast.error('Veri indirme başarısız.');
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    const confirm1 = window.prompt(
      'Hesabınızı KALICI olarak silmek üzeresiniz. Tüm ilerlemeniz, sayfa ezber kayıtlarınız, rozetleriniz silinecek.\n\nDevam etmek için aşağıya "SİL" yazın:'
    );
    if (confirm1 !== 'SİL') {
      toast.info('Silme işlemi iptal edildi.');
      return;
    }
    setDeleting(true);
    try {
      await accountAPI.deleteAccount();
      toast.success('Hesabınız silindi. Hoşçakal 🙏');
      try { localStorage.removeItem('hafizim_token'); } catch (_) { /* ignore */ }
      setTimeout(() => { window.location.href = '/'; }, 1500);
    } catch (e) {
      toast.error('Hesap silinemedi.');
    } finally {
      setDeleting(false);
    }
  };

  const card = `bg-white shadow-sm p-6 ${isKidsMode ? 'rounded-3xl' : 'rounded-lg'} mt-6`;

  return (
    <div className={card} data-testid="kvkk-section">
      <div className="flex items-center gap-2 mb-3">
        <Shield className="w-5 h-5 text-teal-700" />
        <h2 className="text-lg font-semibold text-gray-800">KVKK / Gizlilik Hakları</h2>
      </div>
      <p className="text-xs text-gray-500 mb-5">
        Kişisel Verilerin Korunması Kanunu (KVKK) Madde 11 ve GDPR kapsamında, verileriniz üzerinde aşağıdaki haklara sahipsiniz.
      </p>

      <div className="space-y-3">
        <button
          type="button"
          onClick={() => navigate('/gizlilik-politikasi')}
          data-testid="open-privacy-policy"
          className="w-full flex items-center justify-between p-3 rounded-lg border border-stone-200 hover:bg-stone-50 transition-colors text-left"
        >
          <span className="flex items-center gap-2 text-sm text-gray-800">
            <FileText className="w-4 h-4 text-stone-500" /> Gizlilik Politikası & KVKK Aydınlatma Metni
          </span>
          <span className="text-xs text-stone-400">→</span>
        </button>
        <button
          type="button"
          onClick={() => navigate('/kullanim-kosullari')}
          data-testid="open-terms"
          className="w-full flex items-center justify-between p-3 rounded-lg border border-stone-200 hover:bg-stone-50 transition-colors text-left"
        >
          <span className="flex items-center gap-2 text-sm text-gray-800">
            <FileText className="w-4 h-4 text-stone-500" /> Kullanım Koşulları
          </span>
          <span className="text-xs text-stone-400">→</span>
        </button>
        <button
          type="button"
          onClick={() => navigate('/cerez-politikasi')}
          data-testid="open-cookies"
          className="w-full flex items-center justify-between p-3 rounded-lg border border-stone-200 hover:bg-stone-50 transition-colors text-left"
        >
          <span className="flex items-center gap-2 text-sm text-gray-800">
            <FileText className="w-4 h-4 text-stone-500" /> Çerez Politikası
          </span>
          <span className="text-xs text-stone-400">→</span>
        </button>

        <button
          type="button"
          onClick={handleExport}
          disabled={exporting}
          data-testid="export-data-btn"
          className="w-full flex items-center justify-between p-3 rounded-lg border border-teal-200 bg-teal-50 hover:bg-teal-100 transition-colors text-left disabled:opacity-50"
        >
          <span className="flex items-center gap-2 text-sm text-teal-900 font-medium">
            <Download className="w-4 h-4" /> {exporting ? 'İndiriliyor…' : 'Verilerimi İndir (JSON)'}
          </span>
          <span className="text-xs text-teal-700">KVKK m.11</span>
        </button>

        <button
          type="button"
          onClick={handleDelete}
          disabled={deleting}
          data-testid="delete-account-btn"
          className="w-full flex items-center justify-between p-3 rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 transition-colors text-left disabled:opacity-50"
        >
          <span className="flex items-center gap-2 text-sm text-red-800 font-semibold">
            <Trash2 className="w-4 h-4" /> {deleting ? 'Siliniyor…' : 'Hesabımı Kalıcı Olarak Sil'}
          </span>
          <span className="text-xs text-red-700">KVKK m.7</span>
        </button>
      </div>
    </div>
  );
};

export default Settings;
